import * as lab from '@mui/lab'
import { Link, Typography, DialogProps, Alert } from '@mui/material'
import { useAuth } from '../../common/context/auth'
import React, { useEffect, useState } from 'react'
import { Modal } from 'src/ui'
import Mfa from './Mfa'
import { convertUserAttributesListToLabels } from '../../common/static'
import * as S from './styles'

interface State {
  error?: string
  success?: string
  verifyMfa: boolean
}

interface SetupMfaProps extends DialogProps {
  open: boolean
  enforced: boolean
}

const SetupMfa: React.FC<SetupMfaProps> = ({ open, enforced, ...props }: SetupMfaProps) => {
  const [state, setState] = useState<State>({
    error: undefined,
    success: undefined,
    verifyMfa: false
  })
  const [qrcode, setQRCode] = useState('')
  const { tempUser, setupTOTP } = useAuth()

  const [modals, setModals] = useState({
    verifyMfa: false
  })

  const handleOpen = (modal: string) => {
    setModals((s) => ({ ...s, [modal]: true }))
  }

  //prevent page from closing if enforced is true
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (enforced) {
        e.preventDefault()
        return ''
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [enforced])

  useEffect(() => {
    const getQRCode = async (email: string): Promise<void> => {
      try {
        if (!tempUser) return
        const code = await setupTOTP()
        const env = process.env.REACT_APP_API_URL
        const issuer =
          env?.includes('staging') || env?.includes('localhost')
            ? 'SDC Staging'
            : env?.includes('sandbox')
            ? 'SDC Sandbox'
            : 'SDC'
        setQRCode(`otpauth://totp/${decodeURI(issuer)}:${decodeURI(email)}?secret=${code}&issuer=${issuer}`)
      } catch (error) {
        setState({ error: (error as Error).message, verifyMfa: false })
      }
    }
    tempUser?.getUserAttributes((err, result) => {
      if (result) {
        const email = convertUserAttributesListToLabels(result).email
        getQRCode(email)
      }
    })
  }, [tempUser])

  const handleNextMfaStep = (): void => {
    handleOpen('verifyMfa')
    setState({ verifyMfa: true })
  }

  return open ? (
    <>
      <Modal
        open={!state.verifyMfa}
        {...props}
        disableEscapeKeyDown={enforced}
        onClose={(event, reason) => {
          if (reason && reason === 'backdropClick') return
          props.onClose && props.onClose(event, reason)
        }}
        title="Setup 2-factor Authentication"
        enforced={enforced}
      >
        {state.error && <Alert severity="error">{state.error}</Alert>}
        {enforced && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Multi-factor authentication setup is required to continue. This dialog cannot be closed until setup is
            complete.
          </Alert>
        )}
        <S.Row>
          <Typography variant="body2">
            For better security please follow the instructions below to set up your 2-factor authentication
          </Typography>
        </S.Row>
        <S.StepsList>
          <S.Step>
            Download{' '}
            <Link
              component="a"
              href="https://support.google.com/accounts/answer/1066447"
              target="_blank"
              rel="noopener"
            >
              authenticator
            </Link>{' '}
            to your phone
          </S.Step>
          <S.Step>
            Open the app and click <strong>&quot;begin setup&quot;</strong>
          </S.Step>
          <S.Step>
            Choose <strong>Scan QR code</strong>
          </S.Step>
        </S.StepsList>
        {qrcode && <S.CustomQRCode value={qrcode} size={150} />}
        <S.Row>
          <lab.LoadingButton variant="contained" size="large" onClick={handleNextMfaStep} fullWidth>
            Continue
          </lab.LoadingButton>
        </S.Row>
      </Modal>
      {modals.verifyMfa && <Mfa open={modals.verifyMfa} />}
    </>
  ) : (
    <></>
  )
}

export default SetupMfa
